import type { IconButtonProps as ChakraIconButtonProps } from '@chakra-ui/react';
import { AbsoluteCenter, IconButton as ChakraIconButton, Span } from '@chakra-ui/react';
import * as React from 'react';

import { Spinner } from '../spinner';
import type { ButtonSize } from './button.kds';
import { type IconComponent, IconSlot } from './icon-slot';

interface IconButtonLoadingProps {
  loading?: boolean;
}

/** KDS IconButton variants: `solid` (Filled), `outline` (Outlined), `ghost` (Ghosted). */
export type IconButtonVariant = 'solid' | 'outline' | 'ghost';

export interface IconButtonProps
  extends Omit<ChakraIconButtonProps, 'size' | 'children' | 'aria-label' | 'layout' | 'variant'>,
    IconButtonLoadingProps {
  'aria-label': string;
  size?: ButtonSize;
  variant?: IconButtonVariant;
  /** Icon component — Lucide (e.g. `ChevronLeft`) or Chakra `createIcon`. */
  icon: IconComponent;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(props, ref) {
  const { loading, disabled, icon, size = 'rg', variant = 'solid', colorPalette, ...rest } = props;
  const effectiveColorPalette = colorPalette ?? (variant === 'ghost' ? 'neutral' : undefined);
  const glyph = <IconSlot icon={icon} size={size} />;

  return (
    <ChakraIconButton
      aria-busy={loading || undefined}
      disabled={loading || disabled}
      ref={ref}
      {...({ ...rest, layout: 'icon', size, variant, colorPalette: effectiveColorPalette } as ChakraIconButtonProps)}
    >
      {loading ? (
        <>
          <AbsoluteCenter display="inline-flex">
            <Spinner size="inherit" color="inherit" />
          </AbsoluteCenter>
          <Span opacity={0} aria-hidden>
            {glyph}
          </Span>
        </>
      ) : (
        glyph
      )}
    </ChakraIconButton>
  );
});
